import React from 'react';

import { differenceInSeconds, isBefore } from 'date-fns';

import { useNow } from '@root/lib/hooks/useNow';

/**
 * UseCountdown hook
 * @author filipditrich <ditrich@nfctron.com
 * @returns {UseCountdownResult}
 */
export const useCountdown = (until: string | number | Date): UseCountdownResult => {
	const date = React.useMemo(() => (until instanceof Date ? until : new Date(until)), [until]);
	const now = useNow(1000);

	/** calculate minutes and seconds */
	return React.useMemo(() => {
		if (isBefore(date, now)) return { minutes: 0, seconds: 0, hours: 0, isFinished: true };
		const totalSeconds = differenceInSeconds(date, now);
		const minutesRaw = totalSeconds / 60;
		const hoursRounded = Math.floor(minutesRaw / 60);
		const minutesRounded = Math.floor(minutesRaw - hoursRounded * 60);
		const secondsRounded = Math.floor(totalSeconds - minutesRounded * 60 - hoursRounded * 60 * 60);

		return {
			minutes: minutesRounded,
			seconds: secondsRounded,
			hours: hoursRounded,
			isFinished: false,
		};
	}, [date, now]);
};

/**
 * UseCountdown hook result type
 * @export
 */
export type UseCountdownResult = {
	hours: number;
	minutes: number;
	seconds: number;
	isFinished: boolean;
};
